import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WORLD_PATH } from "@/data/worldPath";

const W = 1000;
const H = 500;
const project = (lon, lat) => [((lon + 180) / 360) * W, ((90 - lat) / 180) * H];
const EASE = [0.22, 1, 0.36, 1];

const prefersReduced =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// HQ origin
const HQ = { name: "Kolkata (HQ)", lon: 88.36, lat: 22.57 };

// Full port network, grouped by region. One designated hub per region drives the main corridor.
const REGIONS = [
  {
    id: "asia",
    label: "Asia",
    hub: "Singapore",
    ports: [
      { name: "Singapore", lon: 103.8, lat: 1.35, hub: true },
      { name: "Port Klang", lon: 101.39, lat: 3.0 },
      { name: "Colombo", lon: 79.86, lat: 6.93 },
      { name: "Ho Chi Minh", lon: 106.66, lat: 10.76 },
      { name: "Manila", lon: 120.98, lat: 14.6 },
      { name: "Hong Kong", lon: 114.17, lat: 22.32 },
      { name: "Shenzhen", lon: 114.06, lat: 22.54 },
      { name: "Ningbo", lon: 121.55, lat: 29.87 },
      { name: "Shanghai", lon: 121.47, lat: 31.23 },
      { name: "Kobe", lon: 135.2, lat: 34.69 },
      { name: "Osaka", lon: 135.5, lat: 34.69 },
      { name: "Tokyo", lon: 139.69, lat: 35.68 },
    ],
  },
  {
    id: "middle-east",
    label: "Middle East",
    hub: "Jebel Ali",
    ports: [
      { name: "Jebel Ali", lon: 55.03, lat: 25.0, hub: true },
      { name: "Sohar", lon: 56.71, lat: 24.43 },
      { name: "Salalah", lon: 54.0, lat: 16.95 },
      { name: "Doha", lon: 51.53, lat: 25.29 },
      { name: "Bahrain", lon: 50.58, lat: 26.15 },
      { name: "Dammam", lon: 50.1, lat: 26.43 },
      { name: "Shuwaikh", lon: 47.93, lat: 29.35 },
      { name: "Jeddah", lon: 39.2, lat: 21.5 },
    ],
  },
  {
    id: "europe",
    label: "Europe",
    hub: "Rotterdam",
    ports: [
      { name: "Rotterdam", lon: 4.47, lat: 51.92, hub: true },
      { name: "Antwerp", lon: 4.4, lat: 51.22 },
      { name: "Felixstowe", lon: 1.35, lat: 51.96 },
      { name: "London", lon: -0.1, lat: 51.5 },
      { name: "Hamburg", lon: 9.99, lat: 53.55 },
      { name: "Copenhagen", lon: 12.57, lat: 55.68 },
      { name: "Oslo", lon: 10.75, lat: 59.91 },
      { name: "Barcelona", lon: 2.17, lat: 41.39 },
    ],
  },
  {
    id: "africa",
    label: "Africa",
    hub: "Djibouti",
    ports: [
      { name: "Djibouti", lon: 43.15, lat: 11.59, hub: true },
      { name: "Mombasa", lon: 39.66, lat: -4.04 },
      { name: "Dar es Salaam", lon: 39.28, lat: -6.79 },
      { name: "Madagascar", lon: 49.4, lat: -18.15 },
      { name: "Lagos", lon: 3.39, lat: 6.45 },
      { name: "Tema", lon: 0.01, lat: 5.63 },
      { name: "Lom\u00e9", lon: 1.22, lat: 6.13 },
      { name: "Cotonou", lon: 2.43, lat: 6.35 },
      { name: "Abidjan", lon: -4.02, lat: 5.32 },
      { name: "Burkina Faso", lon: -1.52, lat: 12.37 },
    ],
  },
  {
    id: "north-america",
    label: "North America",
    hub: "New York",
    ports: [
      { name: "New York", lon: -74.0, lat: 40.71, hub: true },
      { name: "Halifax", lon: -63.57, lat: 44.65 },
      { name: "Chicago", lon: -87.65, lat: 41.85 },
      { name: "Vancouver", lon: -123.12, lat: 49.28 },
      { name: "San Francisco", lon: -122.42, lat: 37.77 },
      { name: "Los Angeles", lon: -118.24, lat: 34.05 },
    ],
  },
  {
    id: "south-america",
    label: "South America",
    hub: "S\u00e3o Paulo",
    ports: [
      { name: "S\u00e3o Paulo", lon: -46.63, lat: -23.55, hub: true },
      { name: "Rio de Janeiro", lon: -43.17, lat: -22.91 },
      { name: "Buenos Aires", lon: -58.38, lat: -34.6 },
    ],
  },
];

const arcPath = (a, b) => {
  const [x1, y1] = project(a.lon, a.lat);
  const [x2, y2] = project(b.lon, b.lat);
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dist = Math.hypot(x2 - x1, y2 - y1);
  const lift = Math.min(dist * 0.26, 90);
  return `M ${x1} ${y1} Q ${mx} ${my - lift} ${x2} ${y2}`;
};

const RouteLine = ({ a, b, main = false, delay = 0 }) => {
  const d = useMemo(() => arcPath(a, b), [a, b]);
  return (
    <g>
      <motion.path
        d={d}
        fill="none"
        stroke={main ? "rgba(90,168,201,0.85)" : "rgba(90,168,201,0.4)"}
        strokeWidth={main ? 1.7 : 0.9}
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 ${main ? 7 : 4}px rgba(90,168,201,0.55))` }}
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ pathLength: { duration: main ? 1.1 : 0.9, delay, ease: EASE }, opacity: { duration: 0.4, delay } }}
      />
      {!prefersReduced && main && (
        <circle r="2.8" fill="#ffffff" style={{ filter: "drop-shadow(0 0 5px rgba(90,168,201,0.9))" }}>
          <animateMotion dur="2.8s" repeatCount="indefinite" path={d} keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
        </circle>
      )}
    </g>
  );
};

export const WorldMap = () => {
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);

  const activeId = hover || pinned || REGIONS[idx].id;
  const region = REGIONS.find((r) => r.id === activeId) || REGIONS[0];
  const hubPort = region.ports.find((p) => p.hub) || region.ports[0];

  // Greedy label de-cluttering: always label the hub, then only label ports
  // that are spatially separated from already-labelled ports to avoid overlap.
  const labeledNames = useMemo(() => {
    const placed = [];
    const result = new Set();
    const ordered = [...region.ports].sort((a, b) => (b.hub ? 1 : 0) - (a.hub ? 1 : 0));
    for (const p of ordered) {
      const [x, y] = project(p.lon, p.lat);
      const ok = placed.every(([px, py]) => Math.hypot(px - x, py - y) >= 30);
      if (p.hub || ok) {
        result.add(p.name);
        placed.push([x, y]);
      }
    }
    return result;
  }, [region]);

  useEffect(() => {
    if (hover || pinned || prefersReduced) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % REGIONS.length), 5000);
    return () => clearInterval(t);
  }, [hover, pinned]);

  return (
    <div data-testid="global-network-map-section" className="relative">
      {/* Corridor chips */}
      <div className="flex flex-wrap items-center gap-2.5 mb-6">
        <span className="eyebrow text-secondary-ssa mr-1">Trade Corridors</span>
        {REGIONS.map((c) => {
          const isActive = activeId === c.id;
          const isPinned = pinned === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`global-network-corridor-chip-${c.id}`}
              onMouseEnter={() => setHover(c.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(c.id)}
              onBlur={() => setHover(null)}
              onClick={() => setPinned((p) => (p === c.id ? null : c.id))}
              aria-pressed={isPinned}
              className={`px-4 h-9 rounded-full text-sm font-medium border transition-colors duration-200 ${
                isActive
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-[color:var(--ssa-border-strong)] hover:border-navy"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-ink grain">
        {/* Floating live-region panel */}
        <div className="absolute left-4 top-4 z-10 pointer-events-none">
          <div className="glass-dark rounded-xl px-4 py-3 min-w-[190px]">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                {!prefersReduced && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-turquoise opacity-60 animate-ping" />
                )}
                <span className="relative inline-flex h-2 w-2 rounded-full bg-turquoise" />
              </span>
              <span className="eyebrow text-turquoise" style={{ letterSpacing: "0.2em" }}>
                Live Network
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={region.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
              >
                <div className="mt-2 font-display text-xl text-white leading-tight">{region.label}</div>
                <div className="mt-0.5 text-xs text-white/55 font-mono-ssa">
                  {region.ports.length} ports &middot; hub {region.hub}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <svg
          data-testid="global-network-map-svg"
          viewBox="0 0 1000 500"
          className="w-full h-auto block"
          role="img"
          aria-label="Animated global maritime network map showing regional shipping corridors"
        >
          <defs>
            <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#5AA8C9" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#5AA8C9" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* subtle lat/long grid */}
          <g stroke="rgba(255,255,255,0.05)" strokeWidth="0.5">
            {Array.from({ length: 9 }).map((_, i) => (
              <line key={`h${i}`} x1="0" x2="1000" y1={(i * H) / 8} y2={(i * H) / 8} />
            ))}
            {Array.from({ length: 13 }).map((_, i) => (
              <line key={`v${i}`} y1="0" y2="500" x1={(i * W) / 12} x2={(i * W) / 12} />
            ))}
          </g>

          {/* continents */}
          <path d={WORLD_PATH} fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.10)" strokeWidth="0.4" />

          {/* faint regional-hub markers (always visible to imply global presence) */}
          {REGIONS.map((r) => {
            const hp = r.ports.find((p) => p.hub) || r.ports[0];
            const [x, y] = project(hp.lon, hp.lat);
            const dim = r.id === activeId;
            return (
              <circle
                key={`base-${r.id}`}
                cx={x}
                cy={y}
                r="2"
                fill="#ffffff"
                opacity={dim ? 0 : 0.32}
                className="transition-opacity duration-500"
              />
            );
          })}

          {/* Active region: progressive reveal of routes, ports and labels */}
          <AnimatePresence mode="wait">
            <motion.g
              key={region.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Main corridor HQ -> regional hub */}
              <RouteLine a={HQ} b={hubPort} main delay={0.05} />

              {/* Supporting routes hub -> ports */}
              {region.ports
                .filter((p) => !p.hub)
                .map((p, i) => (
                  <RouteLine key={`r-${region.id}-${p.name}`} a={hubPort} b={p} delay={0.35 + i * 0.06} />
                ))}

              {/* Port nodes + floating labels */}
              {region.ports.map((p, i) => {
                const [x, y] = project(p.lon, p.lat);
                const anchorEnd = p.lon > 150 || x > 920;
                const offY = i % 2 === 0 ? -7 : 13;
                return (
                  <motion.g
                    key={`p-${region.id}-${p.name}`}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, delay: 0.3 + i * 0.05, ease: EASE }}
                  >
                    <circle cx={x} cy={y} r="13" fill="url(#hubGlow)" opacity={p.hub ? 1 : 0.7} />
                    {p.hub && !prefersReduced && (
                      <circle cx={x} cy={y} r="4" fill="none" stroke="#B89B5E" strokeWidth="1">
                        <animate attributeName="r" from="4" to="14" dur="2.4s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.9" to="0" dur="2.4s" repeatCount="indefinite" />
                      </circle>
                    )}
                    <circle cx={x} cy={y} r={p.hub ? 4 : 2.6} fill={p.hub ? "#B89B5E" : "#FFFFFF"} />
                    {labeledNames.has(p.name) && (
                      <text
                        x={x + (anchorEnd ? -7 : 7)}
                        y={y + offY}
                        fill={p.hub ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.72)"}
                        fontSize={p.hub ? "10" : "8.5"}
                        fontWeight={p.hub ? 600 : 400}
                        textAnchor={anchorEnd ? "end" : "start"}
                        className="font-mono-ssa"
                      >
                        {p.name}
                      </text>
                    )}
                  </motion.g>
                );
              })}
            </motion.g>
          </AnimatePresence>

          {/* HQ marker (always on) */}
          {(() => {
            const [x, y] = project(HQ.lon, HQ.lat);
            return (
              <g>
                <circle cx={x} cy={y} r="16" fill="url(#hubGlow)" />
                <circle cx={x} cy={y} r="4.5" fill="#B89B5E" />
                {!prefersReduced && (
                  <circle cx={x} cy={y} r="4.5" fill="none" stroke="#B89B5E" strokeWidth="1">
                    <animate attributeName="r" from="4.5" to="16" dur="2.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.9" to="0" dur="2.6s" repeatCount="indefinite" />
                  </circle>
                )}
                <text x={x + 8} y={y - 8} fill="#ffffff" fontSize="10.5" fontWeight={600} className="font-mono-ssa">
                  {HQ.name}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
};
